import {
	JobEntityType,
	ClientEntityType,
	OrganizationEntityType,
	JobDocumentEntityType,
	JobItemEntityType,
	BankAccountEntityType,
} from "@/lib/supabase/supabaseTypes";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import { getGuarantorDisplayName } from "../../lib/utils/guarantor-utils";
import { PdfTemplateType } from "../../pdf-templates/document-template.types";
import { documentIsInvoice } from "../../hooks/useHandleExportDocument/export-document-utils";
import { patientHasAddress } from "../../pdf-templates/utils";
import { getBankAccount } from "../../hooks/actions/get-bank-account/get-bank-account";
import {
	documentPropsFor,
	DocumentPropsForReturnType,
	CompleteJobDocumentInformation,
} from "../../hooks/actions/useExportActions";
import { usePatientStore } from "../../store/Patient";
import { TableInvoiceRow } from "./types/invoice-table.types";
import type { FieldProblem } from "../../store/Jobs/field-problems.types";
import {
	getPatientDebtorProblems,
	NO_PATIENT_PROBLEM,
} from "../../store/Jobs/utils";

export const getAccountingRecipient = (value: TableInvoiceRow) => {
	return getClientDisplayName({
		is_company: value.recipient_is_company,
		company_name: value.recipient_company_name,
		first_name: value.recipient_first_name,
		last_name: value.recipient_last_name,
	} as any);
};

export interface PrepareDocumentExportDataParams {
	job: JobEntityType;
	client: ClientEntityType;
	organization: OrganizationEntityType;
	jobDocument: JobDocumentEntityType;
	jobItems: JobItemEntityType[];
	templateType: PdfTemplateType;
	bankAccountsLookup: Record<string, BankAccountEntityType>;
	akontoAmount?: number;
}

export interface PrepareDocumentExportDataResult {
	success: boolean;
	error?: string;
	/** Job-page fields behind `error`; only the job page flags them. */
	problems?: FieldProblem[];
	data?: {
		documentProps: DocumentPropsForReturnType;
		bankAccount: BankAccountEntityType;
		completeJobInformation: CompleteJobDocumentInformation;
	};
}

/**
 * Prepares all necessary data for document export without performing any RPC calls or file operations.
 * Extracted from handleExportDocument to be reusable in ZAK export.
 */
export async function prepareDocumentExportData({
	job,
	client,
	organization,
	jobDocument,
	jobItems,
	templateType,
	bankAccountsLookup,
	akontoAmount,
}: PrepareDocumentExportDataParams): Promise<PrepareDocumentExportDataResult> {
	// Validate required entities
	if (!job) {
		return {
			success: false,
			error: "Auftrag nicht gefunden. Bitte versuchen Sie es erneut.",
		};
	}
	if (!client) {
		return {
			success: false,
			error: "Kunde nicht gefunden. Bitte versuchen Sie es erneut.",
		};
	}
	if (!organization) {
		return {
			success: false,
			error: "Organisation nicht gefunden. Bitte versuchen Sie es erneut.",
		};
	}

	// Validate patient address for patient-paid invoices
	if (job.is_paid_by_patient && documentIsInvoice(templateType)) {
		const patient = job.patient_id
			? await usePatientStore.getState().getPatient(job.patient_id)
			: null;
		if (!patient) {
			return {
				success: false,
				error: job.patient_id
					? "Patient nicht gefunden. Bitte versuchen Sie es erneut."
					: "Bitte wählen Sie einen Patienten aus, bevor Sie die Rechnung erstellen.",
				problems: [NO_PATIENT_PROBLEM],
			};
		}
		if (!patientHasAddress(patient)) {
			return {
				success: false,
				error: "Die Adresse des Patienten ist nicht vollständig. Die Rechnung kann nicht exportiert werden.",
				problems: getPatientDebtorProblems(patient),
			};
		}
	}

	// Prepare complete job information
	const completeJobInformation = {
		client,
		job,
		jobItems,
		jobDocument,
	};

	// Get bank account
	const { bankAccount, error: bankAccountError } = getBankAccount({
		bankAccountsLookup,
		lab: organization,
		job: job,
		recipient: client,
	});
	if (!bankAccount) {
		return {
			success: false,
			error: bankAccountError,
			problems: [
				{ fieldId: "jobs.bank_account_id", message: bankAccountError },
			],
		};
	}

	// Prepare document properties
	const documentProps = await documentPropsFor(
		organization,
		completeJobInformation,
		akontoAmount
	);
	if (!documentProps) {
		return {
			success: false,
			error: "Dokumentinformationen konnten nicht erstellt werden. Bitte versuchen Sie es erneut.",
		};
	}

	return {
		success: true,
		data: {
			documentProps,
			bankAccount,
			completeJobInformation,
		},
	};
}
